/**
 * Timezone configuration and utilities
 * Provides consistent timezone handling across the application
 */

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { getTimezone } from "@/stores/domains/timezone";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

// Default timezone for the application
export const DEFAULT_TIMEZONE = "Asia/Singapore";
const DATE_INPUT_FORMATS = ["DD/MM/YYYY", "YYYY-MM-DD"] as const;
const DATETIME_INPUT_FORMATS = [
  "DD/MM/YYYY HH:mm:ss",
  "DD/MM/YYYY HH:mm",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm",
] as const;
const UTC_API_DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ss[Z]";
const OFFSET_API_DATETIME_FORMAT = "YYYY-MM-DDTHH:mm:ssZ";

type TimeBoundary = "start" | "end";
type DateLikeInput = string | number | Date | dayjs.Dayjs;

interface FormatPickerDateTimeOptions {
  boundary?: TimeBoundary;
  timezone?: string;
  outputFormat?: string;
  hasExplicitTime?: boolean;
}

const resolveTimezone = (timezone?: string): string => {
  return timezone || getBrowserTimezone() || DEFAULT_TIMEZONE;
};

const hasTimeInString = (value: string): boolean => {
  return /\d{1,2}:\d{2}(:\d{2})?/.test(value);
};

const hasExplicitTimeValue = (value: DateLikeInput, hasExplicitTime?: boolean): boolean => {
  if (typeof hasExplicitTime === "boolean") {
    return hasExplicitTime;
  }

  if (typeof value === "string") {
    return hasTimeInString(value);
  }

  const dateValue = dayjs(value);
  return (
    dateValue.hour() !== 0 ||
    dateValue.minute() !== 0 ||
    dateValue.second() !== 0 ||
    dateValue.millisecond() !== 0
  );
};

const tryParseDateInTimezone = (
  dateString: string,
  format: string | undefined,
  timezone: string,
): dayjs.Dayjs | null => {
  try {
    const parsed = format ? dayjs.tz(dateString, format, timezone) : dayjs.tz(dateString, timezone);

    return parsed.isValid() ? parsed : null;
  } catch {
    return null;
  }
};

const parseDateInTimezone = (dateString: string, timezone: string): dayjs.Dayjs => {
  for (const format of [...DATETIME_INPUT_FORMATS, ...DATE_INPUT_FORMATS]) {
    const parsed = tryParseDateInTimezone(dateString, format, timezone);
    if (parsed) {
      return parsed;
    }
  }

  const parsed = tryParseDateInTimezone(dateString, undefined, timezone);
  if (parsed) {
    return parsed;
  }

  return dayjs(dateString);
};

const normalizeOffsetDateString = (value: string): string => {
  const trimmedValue = value.trim();

  const separatedOffsetMatch = trimmedValue.match(
    /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?) ([+-]\d{2})(\d{2})$/,
  );

  if (separatedOffsetMatch) {
    const [, datePart, timePart, offsetHour, offsetMinute] = separatedOffsetMatch;
    return `${datePart}T${timePart}${offsetHour}:${offsetMinute}`;
  }

  const compactOffsetMatch = trimmedValue.match(
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?)([+-]\d{2})(\d{2})$/,
  );

  if (compactOffsetMatch) {
    const [, dateTimePart, offsetHour, offsetMinute] = compactOffsetMatch;
    return `${dateTimePart}${offsetHour}:${offsetMinute}`;
  }

  return trimmedValue;
};

/**
 * Configure dayjs with timezone plugins
 * Note: We don't set a global default timezone to allow dynamic timezone switching
 */
export const configureDayjs = () => {
  // Don't set default timezone - let each component use its own timezone
  // This allows for dynamic timezone switching based on clinic/user location
};

/**
 * Get current timestamp in the application's timezone
 */
export const getCurrentTimestamp = (): number => {
  return dayjs().tz(DEFAULT_TIMEZONE).valueOf();
};

/**
 * Get current timestamp in seconds
 */
export const getCurrentTimestampSeconds = (): number => {
  return Math.floor(getCurrentTimestamp() / 1000);
};

/**
 * Format date with timezone
 */
export const formatDateWithTimezone = (
  date: string | number | Date | dayjs.Dayjs,
  format: string = "YYYY-MM-DD HH:mm:ss",
  timezone: string = DEFAULT_TIMEZONE,
): string => {
  return dayjs(date).tz(timezone).format(format);
};

/**
 * Convert UTC timestamp to local timezone
 */
export const utcToLocal = (
  utcTimestamp: string | number | Date,
  timezone: string = DEFAULT_TIMEZONE,
): dayjs.Dayjs => {
  return dayjs.utc(utcTimestamp).tz(timezone);
};

/**
 * Convert local timestamp to UTC
 */
export const localToUtc = (
  localTimestamp: string | number | Date,
  timezone: string = DEFAULT_TIMEZONE,
): dayjs.Dayjs => {
  return dayjs.tz(localTimestamp, timezone).utc();
};

/**
 * Get timezone offset in minutes
 */
export const getTimezoneOffset = (timezone: string = DEFAULT_TIMEZONE): number => {
  return dayjs().tz(timezone).utcOffset();
};

/**
 * Get timezone abbreviation
 */
export const getTimezoneAbbr = (timezone: string = DEFAULT_TIMEZONE): string => {
  return dayjs().tz(timezone).format("z");
};

/**
 * Check if a date is in daylight saving time
 */
export const isDST = (
  date: string | number | Date = new Date(),
  timezone: string = DEFAULT_TIMEZONE,
): boolean => {
  const winter = dayjs().tz(timezone).month(0).utcOffset();
  const summer = dayjs().tz(timezone).month(6).utcOffset();
  const current = dayjs(date).tz(timezone).utcOffset();

  return Math.max(winter, summer) === current;
};

/**
 * Get user's browser timezone
 */
export const getBrowserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Check if browser timezone matches application timezone
 */
export const isBrowserTimezoneMatched = (): boolean => {
  return getBrowserTimezone() === DEFAULT_TIMEZONE;
};

/**
 * Get timezone-aware timestamp in seconds
 */
export const getTimestampSeconds = (): number => {
  // Get timestamp in seconds, not milliseconds
  return getCurrentTimestampSeconds();
};

/**
 * Format timestamp for logging
 */
export const formatLogTimestamp = (
  timestamp?: string | number | Date,
  timezone: string = DEFAULT_TIMEZONE,
): string => {
  const date = timestamp ? dayjs(timestamp) : dayjs();
  return date.tz(timezone).format("YYYY-MM-DD HH:mm:ss.SSS [GMT]Z");
};

/**
 * Create a timezone-aware Date object
 */
export const createTimezoneDate = (
  input?: string | number | Date,
  timezone: string = DEFAULT_TIMEZONE,
): Date => {
  if (input) {
    return dayjs.tz(input, timezone).toDate();
  }
  return dayjs().tz(timezone).toDate();
};

/**
 * Format date with time for API request
 * Converts local timezone date to UTC format
 * Uses the timezone stored in localStorage (managed by timezone store)
 */
export const formatDateWithTime = (dateString: string, time: "start" | "end"): string => {
  // Get current timezone from store (falls back to DEFAULT_TIMEZONE)
  const currentTimezone = getTimezone() || DEFAULT_TIMEZONE;
  // Treat input date string as local timezone from store
  const localDate = parseDateInTimezone(dateString, currentTimezone);
  // Get start or end of day in local timezone
  const boundaryDate = time === "start" ? localDate.startOf("day") : localDate.endOf("day");
  // Convert to UTC and format
  return boundaryDate.utc().format(UTC_API_DATETIME_FORMAT);
};

/**
 * Convert a date picker value into a browser-timezone datetime string with offset.
 * - If the input only contains a date, it fills `00:00:00` or `23:59:59` by boundary.
 * - If the input already contains time, it keeps the selected time.
 */
export const formatPickerValueToBrowserOffset = (
  value: DateLikeInput | null | undefined,
  options: FormatPickerDateTimeOptions = {},
): string => {
  if (!value) {
    return "";
  }

  const {
    boundary = "start",
    timezone,
    outputFormat = OFFSET_API_DATETIME_FORMAT,
    hasExplicitTime,
  } = options;
  const resolvedTimezone = resolveTimezone(timezone);
  const parsedValue =
    typeof value === "string" ? parseDateInTimezone(value, resolvedTimezone) : dayjs(value);

  if (!parsedValue.isValid()) {
    return "";
  }

  const zonedValue =
    typeof value === "string" ? parsedValue : dayjs(parsedValue.toDate()).tz(resolvedTimezone);

  if (hasExplicitTimeValue(value, hasExplicitTime)) {
    return zonedValue.format(outputFormat);
  }

  return (boundary === "start" ? zonedValue.startOf("day") : zonedValue.endOf("day")).format(
    outputFormat,
  );
};

/**
 * Parse a backend datetime returned as UTC (`Z`) or with an explicit offset (`+08:00`, `+0800`)
 * and display it in the browser timezone.
 */
export const formatServerDateTimeToBrowserDisplay = (
  value: string | number | Date | dayjs.Dayjs | null | undefined,
  format: string = "DD/MM/YYYY HH:mm:ss",
  timezone?: string,
): string => {
  if (!value) {
    return "";
  }

  const resolvedTimezone = resolveTimezone(timezone);
  const parsedValue =
    typeof value === "string" ? dayjs(normalizeOffsetDateString(value)) : dayjs(value);

  if (!parsedValue.isValid()) {
    return "";
  }

  return dayjs(parsedValue.toDate()).tz(resolvedTimezone).format(format);
};

/**
 * Backward-compatible alias for offset-aware server datetimes.
 * Also supports standard UTC ISO strings such as `2026-03-18T04:18:33.182Z`.
 */
export const formatOffsetDateTimeToBrowserDisplay = formatServerDateTimeToBrowserDisplay;

export default {
  DEFAULT_TIMEZONE,
  configureDayjs,
  getCurrentTimestamp,
  getCurrentTimestampSeconds,
  formatDateWithTimezone,
  utcToLocal,
  localToUtc,
  getTimezoneOffset,
  getTimezoneAbbr,
  isDST,
  getBrowserTimezone,
  isBrowserTimezoneMatched,
  getTimestampSeconds,
  formatLogTimestamp,
  createTimezoneDate,
  formatDateWithTime,
  formatServerDateTimeToBrowserDisplay,
  formatPickerValueToBrowserOffset,
  formatOffsetDateTimeToBrowserDisplay,
};
